import React, { useContext, useEffect, useState } from "react";
import {
  Search,
  Filter,
  Plus,
  ArrowUpDown,
  MessageSquare,
  Paperclip,
  AlertCircle,
  Clock,
  CheckCircle,
  XCircle,
  ArrowLeft,
  ArrowRight,
  User,
  Tag,
  Calendar,
  MoreHorizontal,
  Inbox,
  PaperclipIcon,
  Edit,
  Trash,
  ChevronLeft,
} from "lucide-react";
import TicketService from "../../services/TicketService";
import CommentService from "../../services/CommentService";
import { AuthContext } from "../../context/AuthContext";

const TicketPage = () => {
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [currentTab, setCurrentTab] = useState("all");
  const [isLoading, setIsLoading] = useState(true);
    const { user, getTickets, setTickets, tickets, ticketLoading, ticketError, } = useContext(AuthContext);
  

  // States for comment functionality
  const [openCommentDialog, setOpenCommentDialog] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentFiles, setCommentFiles] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState(null);

  // States for ticket edit functionality
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editTicket, setEditTicket] = useState({
    title: "",
    description: "",
    priority: "",
    status: "",
    dueDate: "",
    estimatedTime: "",
    tags: [],
  });
  const [editFiles, setEditFiles] = useState([]);
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    if (!tickets.length) {
      getTickets();
    }
  }, [tickets, getTickets]);

  const filteredTickets = tickets.filter((ticket) => {
    if (currentTab === "my" && ticket.assignedTo !== "Michael Chen")
      return false;
    if (filterStatus !== "all" && ticket.status !== filterStatus) return false;
    if (filterPriority !== "all" && ticket.priority !== filterPriority)
      return false;
    return true;
  });

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "LOW":
        return "bg-gray-100 text-gray-800";
      case "MEDIUM":
        return "bg-blue-100 text-blue-800";
      case "HIGH":
        return "bg-orange-100 text-orange-800";
      case "URGENT":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getPriorityDot = (priority) => {
    switch (priority) {
      case "LOW":
        return "bg-gray-400";
      case "MEDIUM":
        return "bg-blue-500";
      case "HIGH":
        return "bg-orange-500";
      case "URGENT":
        return "bg-red-500";
      default:
        return "bg-gray-400";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "OPEN":
      case "NEW":
        return "bg-blue-100 text-blue-800";
      case "IN_PROGRESS":
        return "bg-yellow-100 text-yellow-800";
      case "PENDING":
        return "bg-purple-100 text-purple-800";
      case "RESOLVED":
      case "COMPLETED":
        return "bg-green-100 text-green-800";
      case "CLOSED":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "OPEN":
      case "NEW":
        return <AlertCircle size={16} className="text-blue-600" />;
      case "IN_PROGRESS":
        return <Clock size={16} className="text-yellow-600" />;
      case "PENDING":
        return <Clock size={16} className="text-purple-600" />;
      case "RESOLVED":
      case "COMPLETED":
        return <CheckCircle size={16} className="text-green-600" />;
      case "CLOSED":
        return <XCircle size={16} className="text-gray-600" />;
      default:
        return <AlertCircle size={16} className="text-gray-600" />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatDateForInput = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toISOString().split("T")[0];
  };

  const formatStatusText = (status) => {
    return status.replace(/_/g, " ");
  };

  const handleTicketSelect = (ticket) => {
    setSelectedTicket(ticket);
  };

  // Comment dialog handlers
  const handleOpenCommentDialog = () => {
    setOpenCommentDialog(true);
    setCommentText("");
    setCommentFiles([]);
    setSuccessMessage("");
    setError(null);
  };

  const handleCloseCommentDialog = () => {
    setOpenCommentDialog(false);
  };

  const handleCommentChange = (e) => {
    setCommentText(e.target.value);
  };

  const handleCommentFileChange = (e) => {
    if (e.target.files) {
      setCommentFiles(Array.from(e.target.files));
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) {
      setError("Comment text is required.");
      return;
    }

    try {
      setSubmitLoading(true);

      // Create comment data
      const commentData = {
        text: commentText,
        ticketId: selectedTicket._id,
      };

      // Add the comment with files if any
      await CommentService.createComment(commentData, commentFiles);

      // Refresh ticket data to show new comment
      const updatedTicket = await TicketService.getTicketById(
        selectedTicket._id
      );
      setSelectedTicket(updatedTicket);

      // Show success message
      setSuccessMessage("Comment added successfully!");

      // Close dialog after a short delay
      setTimeout(() => {
        handleCloseCommentDialog();
        setSuccessMessage("");
      }, 2000);

      setSubmitLoading(false);
    } catch (err) {
      console.error("Error adding comment:", err);
      setError("Failed to add comment. Please try again.");
      setSubmitLoading(false);
    }
  };

  // Edit ticket dialog handlers
  const handleOpenEditDialog = () => {
    if (!selectedTicket) return;

    // Initialize the edit form with the current ticket data
    setEditTicket({
      title: selectedTicket.title || "",
      description: selectedTicket.description || "",
      priority: selectedTicket.priority || "MEDIUM",
      status: selectedTicket.status || "OPEN",
      dueDate: formatDateForInput(selectedTicket.dueDate),
      estimatedTime: selectedTicket.estimatedTime || "",
      tags: selectedTicket.tags || [],
    });

    setOpenEditDialog(true);
    setEditFiles([]);
    setSuccessMessage("");
    setError(null);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditTicket((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTagsChange = (e) => {
    const tagsArray = e.target.value.split(",").map((tag) => tag.trim());
    setEditTicket((prev) => ({
      ...prev,
      tags: tagsArray.filter((tag) => tag !== ""),
    }));
  };

  const handleEditFileChange = (e) => {
    if (e.target.files) {
      setEditFiles(Array.from(e.target.files));
    }
  };

  const handleUpdateTicket = async () => {
    if (!editTicket.title.trim()) {
      setError("Title is required.");
      return;
    }

    try {
      setEditLoading(true);

      // Create a clean update object with only the fields that have changed
      const updates = {};
      if (editTicket.title !== selectedTicket.title)
        updates.title = editTicket.title;
      if (editTicket.description !== selectedTicket.description)
        updates.description = editTicket.description;
      if (editTicket.priority !== selectedTicket.priority)
        updates.priority = editTicket.priority;
      if (editTicket.status !== selectedTicket.status)
        updates.status = editTicket.status;

      // Handle date and time fields
      if (editTicket.dueDate) {
        const currentDate = formatDateForInput(selectedTicket.dueDate);
        if (editTicket.dueDate !== currentDate)
          updates.dueDate = editTicket.dueDate;
      }

      if (editTicket.estimatedTime !== selectedTicket.estimatedTime) {
        updates.estimatedTime = editTicket.estimatedTime;
      }

      // Handle array fields
      if (
        JSON.stringify(editTicket.tags) !==
        JSON.stringify(selectedTicket.tags || [])
      ) {
        updates.tags = editTicket.tags;
      }

      // Update the ticket - no need to change this part
      const updatedTicket = await TicketService.updateTicket(
        selectedTicket._id,
        updates,
        editFiles
      );

      // Update selected ticket and ticket list
      setSelectedTicket(updatedTicket);
      setTickets((prev) =>
        prev.map((ticket) =>
          ticket._id === updatedTicket._id ? updatedTicket : ticket
        )
      );

      // Show success message
      setSuccessMessage("Ticket updated successfully!");

      // Close dialog after a short delay
      setTimeout(() => {
        handleCloseEditDialog();
        setSuccessMessage("");
      }, 2000);

      setEditLoading(false);
    } catch (err) {
      console.error("Error updating ticket:", err);
      setError("Failed to update ticket. Please try again.");
      setEditLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Ticket List Panel */}
        <div
          className={`w-full lg:w-2/5 xl:w-1/3 bg-white border-r border-gray-200 flex flex-col ${
            selectedTicket && "hidden lg:flex"
          }`}
        >
          {/* Header */}
          <div className="p-4 border-b border-gray-200">
            <h1 className="text-xl font-bold text-gray-800">Tickets</h1>
            <div className="flex items-center mt-4">
              <div className="relative flex-grow">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Search size={18} className="text-gray-400" />
                </div>
                <input
                  type="text"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Search tickets..."
                />
              </div>
              <button className="ml-2 p-2 text-gray-500 hover:text-gray-700 rounded-md hover:bg-gray-100 transition-colors">
                <Filter size={20} />
              </button>
              <button className="ml-2 p-2 text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors shadow-sm">
                <Plus size={20} />
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="flex p-4 border-b border-gray-200 bg-gray-50">
            <div className="mr-4 flex-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="block w-full border border-gray-300 rounded-md py-1.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Statuses</option>
                <option value="OPEN">Open</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="PENDING">Pending</option>
                <option value="RESOLVED">Resolved</option>
                <option value="CLOSED">Closed</option>
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                className="block w-full border border-gray-300 rounded-md py-1.5 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Priorities</option>
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
                <option value="URGENT">Urgent</option>
              </select>
            </div>
          </div>

          {/* Ticket List */}
          <div className="flex-1 overflow-auto">
            {ticketLoading ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
                <p className="text-sm font-medium">Loading tickets...</p>
              </div>
            ) : filteredTickets.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-gray-500 p-6">
                <Inbox size={48} className="mb-4 text-gray-400" />
                <p className="text-lg font-medium">No tickets found</p>
                <p className="mt-1 text-sm text-center">
                  Try adjusting your filters or create a new ticket
                </p>
                <button className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors shadow-sm flex items-center">
                  <Plus size={16} className="mr-1" />
                  New Ticket
                </button>
              </div>
            ) : (
              <ul className="divide-y divide-gray-200">
                {filteredTickets.map((ticket) => (
                  <li
                    key={ticket._id}
                    onClick={() => handleTicketSelect(ticket)}
                    className={`cursor-pointer hover:bg-gray-50 transition-colors ${
                      selectedTicket?._id === ticket._id ? "bg-blue-50" : ""
                    }`}
                  >
                    <div className="px-4 py-4">
                      <div className="flex items-center justify-between">
                        <span className="inline-flex items-center text-xs font-medium text-gray-600">
                          {ticket.kkgtTicketId}
                        </span>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(
                            ticket.priority
                          )}`}
                        >
                          {ticket.priority}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="mt-1">
                          <h3 className="text-sm font-medium text-gray-900 line-clamp-1">
                            {ticket.title}
                          </h3>
                          <p className="mt-1 text-xs text-gray-500 line-clamp-2">
                            {ticket.description}
                          </p>
                        </div>
                        <div
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(
                            ticket.status
                          )}`}
                        >
                          {getStatusIcon(ticket.status)}
                          <span className="ml-1">
                            {ticket.status.replace("_", " ")}
                          </span>
                        </div>
                      </div>
                      <div className="mt-2 flex items-center justify-between">
                        <div className="flex items-center">
                          <span
                            className={`w-2 h-2 rounded-full ${getPriorityDot(
                              ticket.priority
                            )}`}
                          ></span>
                          <span className="ml-1.5 text-xs text-gray-500">
                            {ticket.project?.name || "No Project"}
                          </span>
                        </div>

                        <div className="flex items-center ">
                          <Clock size={12} className="mr-1" />
                          <span className="text-xs">
                            {new Date(ticket.updatedAt).toLocaleDateString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              }
                            )}
                          </span>
                        </div>
                      </div>
                      <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                        <div className="flex items-center">
                          <span className="ml-1">
                            {ticket.assignedTo || "Unassigned"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Pagination */}
          <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 bg-white">
            <div className="flex-1 flex justify-between">
              <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                <ArrowLeft size={16} className="mr-2" />
                Previous
              </button>
              <button className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors">
                Next
                <ArrowRight size={16} className="ml-2" />
              </button>
            </div>
          </div>
        </div>

        {/* Ticket Detail Panel */}
        <div
          className={`w-full lg:w-3/5 xl:w-2/3 bg-white flex flex-col ${
            !selectedTicket && "hidden lg:flex"
          }`}
        >
          {selectedTicket ? (
            <>
              {/* Success message */}
              {successMessage && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded m-4">
                  {successMessage}
                </div>
              )}

              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between shadow-sm">
                <div className="flex items-center">
                  <button
                    onClick={() => setSelectedTicket(null)}
                    className="lg:hidden mr-4 p-1 rounded-full text-gray-400 hover:text-gray-500 hover:bg-gray-100 transition-colors"
                  >
                    <ArrowLeft size={20} />
                  </button>
                  <div>
                    <h2 className="text-xl font-bold text-gray-800">
                      Ticket #{selectedTicket.kkgtTicketId}
                    </h2>
                    <p className="mt-1 text-sm text-gray-600">
                      {selectedTicket.project?.name || "No Project"}
                    </p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    className="p-2 text-blue-600 hover:text-blue-800 rounded-full hover:bg-blue-50"
                    onClick={handleOpenEditDialog}
                  >
                    <Edit size={18} />
                  </button>
                  <button className="p-2 text-red-600 hover:text-red-800 rounded-full hover:bg-red-50">
                    <Trash size={18} />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-auto">
                <div className="p-6">
                  {/* Ticket header */}
                  <div className="flex items-center justify-between mb-4">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                        selectedTicket.status
                      )}`}
                    >
                      {getStatusIcon(selectedTicket.status)}
                      <span className="ml-1">
                        {formatStatusText(selectedTicket.status)}
                      </span>
                    </span>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(
                        selectedTicket.priority
                      )}`}
                    >
                      {selectedTicket.priority}
                    </span>
                  </div>

                  {/* Ticket title and description */}
                  <div className="mb-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {selectedTicket.title}
                    </h3>
                    <p className="text-gray-700 whitespace-pre-line">
                      {selectedTicket.description}
                    </p>
                  </div>

                  {/* Ticket metadata */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div className="bg-gray-50 rounded-lg p-4 shadow-sm">
                      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                        <Tag size={14} className="mr-1.5 text-gray-500" />
                        Ticket Details
                      </h4>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">
                            Priority:
                          </span>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(
                              selectedTicket.priority
                            )}`}
                          >
                            {selectedTicket.priority}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">Status:</span>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                              selectedTicket.status
                            )}`}
                          >
                            {getStatusIcon(selectedTicket.status)}
                            <span className="ml-1">
                              {formatStatusText(selectedTicket.status)}
                            </span>
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">
                            Created:
                          </span>
                          <span className="text-sm text-gray-700">
                            {formatDate(selectedTicket.createdAt)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">
                            Updated:
                          </span>
                          <span className="text-sm text-gray-700">
                            {formatDate(selectedTicket.updatedAt)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">
                            Due Date:
                          </span>
                          <span className="text-sm text-gray-700 flex items-center">
                            <Calendar
                              size={12}
                              className="mr-1 text-gray-500"
                            />
                            {formatDate(selectedTicket.dueDate)}
                          </span>
                        </div>
                        {selectedTicket.estimatedTime && (
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-500">
                              Estimated Time:
                            </span>
                            <span className="text-sm text-gray-700 flex items-center">
                              <Clock size={12} className="mr-1 text-gray-500" />
                              {selectedTicket.estimatedTime} hours
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 shadow-sm">
                      <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                        <User size={14} className="mr-1.5 text-gray-500" />
                        People
                      </h4>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">
                            Created By:
                          </span>
                          <div className="flex items-center">
                            <div className="rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                              {selectedTicket.createdBy ? (
                                <img
                                  className="w-6 h-6 rounded-full "
                                  src={`https://ui-avatars.com/api/?name=${
                                    selectedTicket.createdBy.name || "User"
                                  }&background=random`}
                                  alt="Avatar"
                                />
                              ) : (
                                <User size={14} className="text-gray-500" />
                              )}
                            </div>
                            <span className="ml-2 text-sm text-gray-700">
                              {selectedTicket.createdBy?.name || "Unknown"}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">
                            Assigned To:
                          </span>
                          <div className="flex items-center">
                            <div className="h-6 w-6 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                              {selectedTicket.assignedTo ? (
                                <img
                                  className="h-full w-full object-cover"
                                  src="/api/placeholder/24/24"
                                  alt={selectedTicket.assignedTo}
                                />
                              ) : (
                                <User size={14} className="text-gray-500" />
                              )}
                            </div>
                            <span className="ml-2 text-sm text-gray-700">
                              {selectedTicket.assignedTo || "Unassigned"}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-500">Client:</span>
                          <span className="text-sm text-gray-700">
                            {selectedTicket.project?.client || "N/A"}
                          </span>
                        </div>
                      </div>

                      {selectedTicket.tags &&
                        selectedTicket.tags.length > 0 && (
                          <div className="mt-4">
                            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                              <Tag size={14} className="mr-1.5 text-gray-500" />
                              Tags
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {selectedTicket.tags.map((tag, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>

                  {/* Attachments */}
                  {selectedTicket.attachment &&
                    selectedTicket.attachment.length > 0 && (
                      <div className="mb-6">
                        <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                          <Paperclip
                            size={14}
                            className="mr-1.5 text-gray-500"
                          />
                          Attachments
                        </h4>
                        <div className="space-y-2">
                          {selectedTicket.attachment.map((file, index) => (
                            <div
                              key={index}
                              className="flex items-center p-2 border rounded-md"
                            >
                              <PaperclipIcon
                                size={16}
                                className="mr-2 text-gray-500"
                              />
                              <a
                                href={file.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:underline cursor-pointer flex items-center"
                              >
                                {file.fileName}
                                {file.fileSize && (
                                  <span className="text-xs text-gray-500 ml-2">
                                    ({Math.round(file.fileSize / 1024)} KB)
                                  </span>
                                )}
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  {/* Comments section */}
                  <div className="mb-6">
                    <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                      <MessageSquare
                        size={14}
                        className="mr-1.5 text-gray-500"
                      />
                      Comments
                    </h4>
                    {selectedTicket.comment &&
                    selectedTicket.comment.length > 0 ? (
                      <div className="space-y-4">
                        {selectedTicket.comment.map((comment, index) => (
                          <div
                            key={index}
                            className="p-3 bg-gray-50 rounded-md"
                          >
                            <div className="flex items-center mb-2">
                              <img
                                className="w-6 h-6 rounded-full mr-2"
                                src={`https://ui-avatars.com/api/?name=${
                                  comment.createdBy?.name || "User"
                                }&background=random`}
                                alt="Avatar"
                              />
                              <span className="text-sm font-medium text-gray-800">
                                {comment.createdBy?.name || "Unknown User"}
                              </span>
                              <span className="text-xs text-gray-500 ml-2">
                                {new Date(comment.createdAt).toLocaleDateString(
                                  "en-US",
                                  {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  }
                                )}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600">
                              {comment.text}
                            </p>

                            {/* Comment attachments if any */}
                            {comment.attachment &&
                              comment.attachment.length > 0 && (
                                <div className="mt-2">
                                  <div className="text-xs font-medium text-gray-500 mb-1">
                                    Attachments:
                                  </div>
                                  <div className="flex flex-wrap gap-2">
                                    {comment.attachment.map(
                                      (file, fileIndex) => (
                                        <a
                                          key={fileIndex}
                                          href={file.fileUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-xs bg-gray-100 text-blue-700 px-2 py-1 rounded flex items-center hover:bg-gray-200"
                                        >
                                          <PaperclipIcon
                                            size={12}
                                            className="mr-1"
                                          />
                                          {file.fileName}
                                        </a>
                                      )
                                    )}
                                  </div>
                                </div>
                              )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500 italic mb-4">
                        No comments yet
                      </div>
                    )}

                    <button
                      className="mt-4 px-4 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 flex items-center"
                      onClick={handleOpenCommentDialog}
                    >
                      <MessageSquare size={16} className="mr-1" />
                      Add Comment
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full bg-gray-50 p-6">
              <div className="bg-white rounded-lg shadow-sm p-10 text-center max-w-md">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageSquare size={24} className="text-blue-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-800 mb-2">
                  No Ticket Selected
                </h2>
                <p className="text-gray-600 mb-6">
                  Select a ticket from the list to view its details and updates.
                </p>
                <div className="flex justify-center">
                  <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors shadow-sm flex items-center">
                    <Plus size={16} className="mr-1" />
                    Create New Ticket
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Comment Dialog */}
      {openCommentDialog && selectedTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold">Add Comment</h3>
            </div>

            <div className="p-6">
              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Comment *
                </label>
                <textarea
                  rows="4"
                  value={commentText}
                  onChange={handleCommentChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your comment here..."
                  required
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Attachments (Optional)
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="comment-file-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none"
                      >
                        <span>Upload files</span>
                        <input
                          id="comment-file-upload"
                          name="comment-file-upload"
                          type="file"
                          className="sr-only"
                          multiple
                          onChange={handleCommentFileChange}
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      PNG, JPG, PDF up to 10MB
                    </p>
                  </div>
                </div>

                {commentFiles.length > 0 && (
                  <div className="mt-3">
                    <h4 className="text-sm font-medium text-gray-700">
                      Selected Files:
                    </h4>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {commentFiles.map((file, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full"
                        >
                          {file.name}
                          <button
                            type="button"
                            className="ml-1 text-gray-500 hover:text-gray-700"
                            onClick={() => {
                              const newFiles = [...commentFiles];
                              newFiles.splice(index, 1);
                              setCommentFiles(newFiles);
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={handleCloseCommentDialog}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium text-white rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  submitLoading || !commentText.trim()
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
                onClick={handleAddComment}
                disabled={submitLoading || !commentText.trim()}
              >
                {submitLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </div>
                ) : (
                  "Add Comment"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Ticket Dialog */}
      {openEditDialog && selectedTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold">Edit Ticket</h3>
              <button
                onClick={handleCloseEditDialog}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <div className="p-6">
              {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={editTicket.title}
                    onChange={handleEditChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Due Date
                  </label>
                  <input
                    type="date"
                    name="dueDate"
                    value={editTicket.dueDate}
                    onChange={handleEditChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority
                  </label>
                  <select
                    name="priority"
                    value={editTicket.priority}
                    onChange={handleEditChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="LOW">Low</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="HIGH">High</option>
                    <option value="URGENT">Urgent</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    name="status"
                    value={editTicket.status}
                    onChange={handleEditChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="OPEN">Open</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="PENDING">Pending</option>
                    <option value="RESOLVED">Resolved</option>
                    <option value="CLOSED">Closed</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estimated Time (hours)
                  </label>
                  <input
                    type="number"
                    name="estimatedTime"
                    value={editTicket.estimatedTime}
                    onChange={handleEditChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    step="0.5"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tags (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={editTicket.tags.join(", ")}
                    onChange={handleTagsChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="bug, feature, frontend"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  rows="4"
                  name="description"
                  value={editTicket.description}
                  onChange={handleEditChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                ></textarea>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Add New Attachments (Optional)
                </label>
                <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="edit-file-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none"
                      >
                        <span>Upload files</span>
                        <input
                          id="edit-file-upload"
                          name="edit-file-upload"
                          type="file"
                          className="sr-only"
                          multiple
                          onChange={handleEditFileChange}
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">
                      PNG, JPG, PDF up to 10MB
                    </p>
                  </div>
                </div>

                {editFiles.length > 0 && (
                  <div className="mt-3">
                    <h4 className="text-sm font-medium text-gray-700">
                      Selected Files:
                    </h4>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {editFiles.map((file, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center bg-gray-100 text-gray-800 text-xs px-2 py-1 rounded-full"
                        >
                          {file.name}
                          <button
                            type="button"
                            className="ml-1 text-gray-500 hover:text-gray-700"
                            onClick={() => {
                              const newFiles = [...editFiles];
                              newFiles.splice(index, 1);
                              setEditFiles(newFiles);
                            }}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Current attachments display */}
              {selectedTicket.attachment &&
                selectedTicket.attachment.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Current Attachments:
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedTicket.attachment.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center bg-gray-50 px-3 py-2 rounded"
                        >
                          <PaperclipIcon
                            size={14}
                            className="mr-2 text-gray-500"
                          />
                          <span className="text-xs text-gray-700">
                            {file.fileName}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                type="button"
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                onClick={handleCloseEditDialog}
              >
                Cancel
              </button>
              <button
                type="button"
                className={`px-4 py-2 text-sm font-medium text-white rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  editLoading || !editTicket.title.trim()
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
                onClick={handleUpdateTicket}
                disabled={editLoading || !editTicket.title.trim()}
              >
                {editLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Updating...
                  </div>
                ) : (
                  "Update Ticket"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketPage;
